import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_GET_ORGANIZATION_CREDITS, KEY_GET_ORGANIZATION_CREDITS } from '../constants';
import { MOCK_RESPONSE_GET_ORGANIZATION_CREDITS } from '../mocks';

type RequestGetOrganizationCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY
>['request']['path'];

export type ResponseGetOrganizationCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY
>['response']['body'];

export type CallParamsGetOrganizationCredits = Omit<
  RequestGetOrganizationCredits,
  'organizationId'
> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetOrganizationCredits = Omit<ResponseGetOrganizationCredits, 'organizationId'>;

type CallErrorGetOrganizationCredits = {
  message: string;
  code: number;
};

type CallSelectorsGetOrganizationCredits = {
  hasNoData: boolean;
  stats: CallResponseGetOrganizationCredits['stats'];
  clusterUsers: CallResponseGetOrganizationCredits['stats']['clusterUsers'];
  departments: CallResponseGetOrganizationCredits['departments'];
};

export const getOrganizationCreditsCall = createGeneralCall<
  CallParamsGetOrganizationCredits,
  CallResponseGetOrganizationCredits,
  CallErrorGetOrganizationCredits,
  CallSelectorsGetOrganizationCredits
>({
  id: KEY_GET_ORGANIZATION_CREDITS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_ORGANIZATION_CREDITS(org as unknown as string, payload.year, payload.quarter);
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_ORGANIZATION_CREDITS : undefined,
  selectors: {
    hasNoData: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.stats?.assigned === 0 &&
          res?.departments?.length === 0 &&
          res?.stats?.clusterUsers?.length === 0,
      ),
    stats: ({ selectors }) => createSelector(selectors.response, res => res?.stats),
    clusterUsers: ({ selectors }) =>
      createSelector(selectors.response, res => res?.stats?.clusterUsers || []),
    departments: ({ selectors }) =>
      createSelector(selectors.response, res => res?.departments || []),
  },
});
